import { Head, useForm } from "@inertiajs/react";
import { LoaderCircle, Upload } from "lucide-react";
import type { FormEventHandler } from "react";

import InputError from "@/components/input-error";
import SupplierTemplateBanner from "@/components/supplier-template-banner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import AppLayout from "@/layouts/app-layout";
import { suppliersPath } from "@/routes";
import type { BreadcrumbItem } from "@/types";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Proveedores",
    href: suppliersPath(),
  },
  {
    title: "Importar",
    href: "/suppliers/import",
  },
];

export default function Import() {
  const { data, setData, post, processing, errors, reset } = useForm({
    file: null as File | null,
  });

  const submit: FormEventHandler = (e) => {
    e.preventDefault();

    if (!data.file) {
      return;
    }

    post("/suppliers/import", {
      forceFormData: true,
      onFinish: () => reset("file"),
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setData("file", file);
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Importar Proveedores" />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex flex-col space-y-2">
          <h2 className="text-2xl font-bold">Importar Proveedores</h2>
          <p className="text-muted-foreground">
            Cargar archivo Excel o CSV para importar proveedores al sistema
          </p>
        </div>

        <SupplierTemplateBanner />

        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Cargar Archivo de Proveedores</CardTitle>
              <CardDescription>
                Selecciona un archivo Excel (.xlsx, .xls) o CSV (.csv) con los
                proveedores a importar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="flex flex-col gap-6" onSubmit={submit}>
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">
                        Formato del archivo
                      </h4>
                      <p className="text-sm text-blue-800 mb-3">
                        El archivo debe contener las siguientes columnas
                        principales:
                      </p>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>
                          • <strong>Razón Social / Name</strong> (requerido) -
                          Nombre de la empresa
                        </li>
                        <li>
                          • <strong>Email</strong> (opcional) - Correo
                          electrónico de contacto
                        </li>
                        <li>
                          • <strong>Teléfono / Phone</strong> (opcional) -
                          Número de teléfono
                        </li>
                        <li>
                          • <strong>Dirección / Address</strong> (opcional) -
                          Dirección física
                        </li>
                        <li>
                          • <strong>CUIT / Tax ID</strong> (opcional) - Número
                          de identificación fiscal
                        </li>
                        <li>
                          • <strong>Contacto / Contact Person</strong>{" "}
                          (opcional) - Persona de contacto
                        </li>
                      </ul>
                      <p className="text-xs text-blue-700 mt-3">
                        * El sistema soporta columnas adicionales como: Nombre
                        Comercial, Rubro, Ciudad, Provincia, País, Sitio Web,
                        etc.
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        * Las columnas pueden tener nombres en español o inglés.
                        El sistema las mapeará automáticamente.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <div className="w-full">
                      <div className="flex justify-center items-center w-full h-32 border-dashed border-2 border-gray-200 rounded-lg">
                        <div className="flex items-center flex-col gap-1.5">
                          <div className="flex items-center flex-row gap-0.5 text-sm font-medium">
                            <Upload className="mr-2 h-4 w-4" />
                            Seleccionar archivo
                          </div>
                          <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileChange}
                            disabled={processing}
                            className="mt-2"
                          />
                        </div>
                      </div>

                      {data.file && (
                        <div className="mt-2 p-2 border border-gray-200 rounded-lg">
                          <p className="text-sm font-medium">
                            Archivo seleccionado:
                          </p>
                          <p className="text-sm text-gray-600">
                            {data.file.name}
                          </p>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Solo se permite un archivo Excel (.xlsx, .xls) o CSV
                      (.csv)
                    </p>
                    <InputError message={errors.file} />
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={processing || !data.file}
                    onClick={(e) => {
                      if (
                        !window.confirm(
                          "Esto procesará el archivo e importará los proveedores. ¿Continuar?",
                        )
                      ) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {processing && (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    )}
                    Importar Proveedores
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.history.back()}
                    disabled={processing}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
